import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminConsole from './pages/AdminConsole';
import ForceChangePassword from './pages/ForceChangePassword';
import ActivityTracker from './components/ActivityTracker';
import { PermProvider } from './components/ui/tabs';
import axios from 'axios';
import LoginPage from './pages/LoginPage';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AboutPage from './pages/AboutPage';
import DesignDepartment from './pages/DesignDepartment';
import ConstructionDepartment from './pages/ConstructionDepartment';
import AdminDepartment from './pages/AdminDepartment';
import EmployeeHub from './pages/EmployeeHub';
import AccountingPage from './pages/AccountingPage';
import SupplyChainPage from './pages/SupplyChainPage';
import { Toaster } from './components/ui/sonner';
import './App.css';

const API_URL = '';

const PAGE_PATHS = [
  ['dashboard', '/dashboard'], ['about', '/about'], ['design', '/design'], ['construction', '/construction'],
  ['admin', '/admin'], ['finance', '/accounting'], ['supply-chain', '/supply-chain'], ['employee-hub', '/employee-hub'],
];

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Apply theme to <html>
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('light');
  const [permKeys, setPermKeys] = useState(null);

  useEffect(() => {
    if (!user) { setPermKeys(null); return; }
    axios.get(`${API_URL}/api/auth/permission-catalog`)
      .then(r => setPermKeys(new Set(r.data.keys)))
      .catch(() => setPermKeys(new Set()));
  }, [user?.id]);

  // Apply theme on mount (before user loads, check localStorage for instant paint)
  useEffect(() => {
    const cached = localStorage.getItem('theme') || 'light';
    setTheme(cached);
    applyTheme(cached);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setUser(data);
          const userTheme = data.theme_preference || 'light';
          setTheme(userTheme);
          applyTheme(userTheme);
          localStorage.setItem('theme', userTheme);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    const userTheme = userData.theme_preference || 'light';
    setTheme(userTheme);
    applyTheme(userTheme);
    localStorage.setItem('theme', userTheme);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('theme');
    setUser(null);
    applyTheme('light');
    setTheme('light');
  };

  const handleThemeToggle = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    try {
      const formData = new FormData();
      formData.append('theme', newTheme);
      await axios.put(`${API_URL}/api/users/theme`, formData);
    } catch (err) {
      console.error('Failed to save theme preference:', err);
    }
  };

  const perms = new Set(user?.permissions || []);
  const isConsole = user?.role === 'superadmin';
  const home = !user ? '/login' : isConsole ? '/admin-console' : (PAGE_PATHS.find(([k]) => perms.has(k)) || [null, '/no-access'])[1];
  const pageProps = { user, onLogout: handleLogout, theme, onThemeToggle: handleThemeToggle };
  const guard = (key, Page) => {
    if (!user) return <Navigate to="/login" />;
    if (isConsole || !perms.has(key)) return <Navigate to={home} replace />;
    return <Page {...pageProps} />;
  };

  if (loading || (user && !permKeys)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground text-lg">Loading...</div>
      </div>
    );
  }

  if (user?.must_change_password) {
    return (
      <>
        <Toaster position="top-right" />
        <ForceChangePassword user={user} onDone={() => setUser({ ...user, must_change_password: false })} onLogout={handleLogout} />
      </>
    );
  }

  return (
    <PermProvider perms={perms} keys={permKeys || new Set()}>
    <Router>
      <Toaster position="top-right" />
      {user && <ActivityTracker canView={(path) => isConsole ? path === '/admin-console' : PAGE_PATHS.some(([k, p]) => p === path && perms.has(k))} />}
      <Routes>
        <Route path="/login" element={user ? <Navigate to={home} replace /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={guard('dashboard', Dashboard)} />
        <Route path="/about" element={guard('about', AboutPage)} />
        <Route path="/design" element={guard('design', DesignDepartment)} />
        <Route path="/construction" element={guard('construction', ConstructionDepartment)} />
        <Route path="/admin" element={guard('admin', AdminDepartment)} />
        <Route path="/employee-hub" element={guard('employee-hub', EmployeeHub)} />
        <Route path="/accounting" element={guard('finance', AccountingPage)} />
        <Route path="/supply-chain" element={guard('supply-chain', SupplyChainPage)} />
        <Route path="/admin-console" element={!user ? <Navigate to="/login" /> : isConsole ? <AdminConsole {...pageProps} /> : <Navigate to={home} replace />} />
        <Route path="/no-access" element={!user ? <Navigate to="/login" /> : (
          <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="no-access-page">
            <p className="text-lg font-semibold">No pages have been assigned to your account yet.</p>
            <p className="text-sm text-muted-foreground">Please contact your administrator.</p>
            <button onClick={handleLogout} className="text-primary underline text-sm" data-testid="no-access-logout">Log out</button>
          </div>
        )} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </Router>
    </PermProvider>
  );
}

export default App;
